import { useState, useCallback, type Dispatch, type SetStateAction } from 'react'
import { uploadWebsites } from '@/api/docsAPI'
import type { OrgDocResponse, WebsiteUploadResult } from '@/models/Orgs'
import type { UUID } from 'crypto'

// ── Types ────────────────────────────────────────────────────

export type PageStatus = 'pending' | 'processing' | 'ingested' | 'error'

export interface PageEntry {
  url: string
  title?: string
  status: PageStatus
  error?: string
  docId?: number
  dateAdded?: string
  /**
   * The crawl job that owns this page. Persisted into
   * `onboarding_progress.wizard_data`, which is authoritative on hydration
   * (OnboardingShell), so the job survives unmount, reload and a different
   * device — the wizard can reconnect to a crawl it no longer remembers
   * starting. Absent on rows written before this field existed, and on pages
   * never submitted; both reconcile as "no live job".
   */
  jobId?: string
  /** An `error` page that may succeed if submitted again. */
  retryable?: boolean
  /**
   * Already resubmitted once from the failed-pages notice. A page gets one
   * retry; persisted so a reload does not hand out a second.
   */
  retried?: boolean
}

/**
 * A crawl job as the wizard sees it. `missing` is a job the server no longer
 * has — past its 24h TTL, or never created — which is not a failure, just an
 * unknown outcome.
 */
export type UploadJobState =
  | 'queued'
  | 'running'
  | 'completed'
  | 'error'
  | 'cleaned_up'
  | 'missing'

export interface UploadJobSnapshot {
  status: UploadJobState
  results?: WebsiteUploadResult[]
}

/** Server-side failure. Never used for a job whose outcome we don't know. */
export const CRAWL_FAILED_ERROR = 'Website upload failed'
/** The POST itself never landed, so no crawl was started. */
export const CRAWL_NOT_STARTED_ERROR = 'Could not start website upload'

const LIVE_JOB_STATES: ReadonlySet<UploadJobState> = new Set([
  'queued',
  'running',
])

// ── Helpers ──────────────────────────────────────────────────

/**
 * The form two URLs are compared in. The server stores `website_url` with the
 * trailing slash cut off the path, so `/about/` must match a stored `/about`.
 */
export function urlKey(url: string): string {
  try {
    const parsed = new URL(/^https?:\/\//.test(url) ? url : `https://${url}`)
    return `${parsed.host}${parsed.pathname.replace(/\/+$/, '')}${parsed.search}`
  } catch {
    return url.replace(/^https?:\/\//, '').replace(/\/+$/, '')
  }
}

function markUrls(
  pages: PageEntry[],
  urls: string[],
  change: Pick<PageEntry, 'status' | 'error' | 'jobId' | 'retryable'>
): PageEntry[] {
  const urlSet = new Set(urls)
  return pages.map((p) => (urlSet.has(p.url) ? { ...p, ...change } : p))
}

function formatDateAdded(value: Date | string | undefined): string | undefined {
  if (!value) return undefined
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return undefined
  return date.toLocaleDateString('en-NZ', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  })
}

// ── Reconcile ────────────────────────────────────────────────

/**
 * Rebuild page statuses from the two sources that actually know: the DB, and
 * the crawl job store.
 *
 * The DB wins. A page with an `org_docs` row was ingested, whatever the wizard
 * cached — including a page the wizard gave up on and marked `error`, which is
 * why an org could sit looking at nine red rows for content that landed days
 * earlier.
 *
 * Where the DB is silent, the job decides, and only a job we can see the
 * outcome of. A live job means `processing`; a job the server has forgotten
 * means `pending` (retryable), not `error`. Only a job that reported the
 * failure itself produces `error`.
 *
 * @param jobs statuses for jobs we've looked up. A jobId absent from the map
 *   hasn't been fetched yet, so its pages are left untouched rather than
 *   guessed at.
 */
export function reconcilePages(
  pages: PageEntry[],
  websiteDocs: OrgDocResponse[],
  jobs: Map<string, UploadJobSnapshot>
): PageEntry[] {
  const docByUrl = new Map<string, OrgDocResponse>()
  for (const doc of websiteDocs) {
    if (doc.website_url) docByUrl.set(urlKey(doc.website_url), doc)
  }

  const reconciled: PageEntry[] = []
  for (const page of pages) {
    const doc = docByUrl.get(urlKey(page.url))
    if (doc) {
      reconciled.push({
        ...page,
        status: 'ingested',
        docId: doc.id,
        dateAdded: formatDateAdded(doc.date_added) ?? page.dateAdded,
        error: undefined,
        jobId: undefined,
        retryable: undefined,
      })
      continue
    }

    // Claimed ingested but no row backs it — the doc was deleted from the
    // profile routes, so drop the page rather than show a broken docId.
    if (page.status === 'ingested') continue

    const job = page.jobId ? jobs.get(page.jobId) : undefined
    if (!page.jobId) {
      // No job was ever recorded against this page — a row written before
      // jobId existed, or one whose submit never landed. Nothing will ever
      // report on it, so a 'processing' here is a spinner that outlives the
      // session that started it. Send it back to pending, which is retryable.
      reconciled.push(
        page.status === 'processing'
          ? { ...page, status: 'pending', error: undefined }
          : page
      )
      continue
    }
    if (!job) {
      // Recorded, but not looked up yet. Leave it alone rather than guess.
      reconciled.push(page)
      continue
    }

    if (LIVE_JOB_STATES.has(job.status)) {
      reconciled.push({ ...page, status: 'processing', error: undefined })
      continue
    }

    if (job.status === 'completed') {
      const failure = job.results?.find(
        (r) => urlKey(r.website) === urlKey(page.url) && !r.success
      )
      // Finished, no doc, no reported failure: the page was crawled and
      // filtered out server-side. Offer a retry rather than a false failure.
      reconciled.push(
        failure
          ? {
              ...page,
              status: 'error',
              error: failure.error,
              jobId: undefined,
              retryable: failure.retryable === true,
            }
          : { ...page, status: 'pending', error: undefined, jobId: undefined }
      )
      continue
    }

    if (job.status === 'error') {
      reconciled.push({
        ...page,
        status: 'error',
        error: CRAWL_FAILED_ERROR,
        jobId: undefined,
        retryable: true,
      })
      continue
    }

    // cleaned_up / missing — outcome unknown, so back to pending.
    reconciled.push({
      ...page,
      status: 'pending',
      error: undefined,
      jobId: undefined,
    })
  }

  return reconciled
}

// ── Hook ─────────────────────────────────────────────────────

/**
 * Hand a batch of URLs to the crawl queue and stop caring.
 *
 * `POST /orgs/{uuid}/websites` returns 202 the moment the job is enqueued; the
 * crawl runs in a worker for minutes afterwards. Waiting on that here is what
 * pinned users to UPLOAD_WEBPAGES — and waiting badly is what told them
 * "Network error"
 * for pages that ingested fine. So this submits, records the job id on each
 * page, and returns. Watching is somebody else's job (OnboardingUploadWatcher).
 */
export function useBatchScrape(
  orgUuid: string,
  getToken: () => Promise<string>
) {
  const [isSubmitting, setIsSubmitting] = useState(false)

  /** Returns the job id, or null when the submit itself failed. */
  const submit = useCallback(
    async (
      urls: string[],
      setPages: Dispatch<SetStateAction<PageEntry[]>>
    ): Promise<string | null> => {
      if (urls.length === 0) return null
      setIsSubmitting(true)
      try {
        const token = await getToken()
        const job = await uploadWebsites({
          token,
          websites: urls,
          orgUuid: orgUuid as UUID,
          // The user picked these pages; a site crawl would store others.
          singlePage: true,
        })
        setPages((prev) =>
          markUrls(prev, urls, {
            status: 'processing',
            error: undefined,
            jobId: job.job_id,
            retryable: undefined,
          })
        )
        return job.job_id
      } catch {
        // Nothing was enqueued, so this failure is real and retryable.
        setPages((prev) =>
          markUrls(prev, urls, {
            status: 'error',
            error: CRAWL_NOT_STARTED_ERROR,
            jobId: undefined,
            retryable: true,
          })
        )
        return null
      } finally {
        setIsSubmitting(false)
      }
    },
    [orgUuid, getToken]
  )

  return { isSubmitting, submit }
}
